/**
 * AuthHubService — DrawLab integration with the DDD Auth Hub.
 *
 * Provides Single Sign-On (SSO) across DDD apps. Users authenticate at
 * auth.divedeepdialogues.com and are redirected back with a custom token
 * that signs them into DrawLab.
 *
 * Architecture:
 * - DrawLab has its own Firebase project (ddd-draw-lab) for RTDB + Storage
 * - Auth Hub has its own Firebase project (ddd-auth-hub) for user identity
 * - When a user signs in via the hub, we get a token signed against the
 *   hub project and use it to sign in to our secondary authHubAuth instance
 * - Anonymous users continue to use the DrawLab project's anonymous auth
 * - Once signed in via hub, the user's entitlements come from JWT claims
 *
 * Australian English throughout.
 */

import { signInWithCustomToken, onAuthStateChanged, signOut as fbSignOut } from 'firebase/auth';
import { authHubAuth, useAuthHub } from '@/firebase';
import { getUrlSecret, clearUrlSecrets, HUB_URL_PARAMS } from '../utils/urlSecrets.js';

class AuthHubService {
  constructor() {
    this.hubUrl = import.meta.env.VITE_AUTH_HUB_URL || 'https://auth.divedeepdialogues.com';
    this.appId = 'draw-lab';
    this.appName = 'DrawLab';
    this.currentUser = null;
    this.userClaims = null;
  }

  /**
   * Is Auth Hub integration enabled?
   * Returns false if the env vars aren't set — allows DrawLab to run in
   * anonymous-only mode when auth hub isn't configured (e.g. local dev).
   */
  isEnabled() {
    return useAuthHub && !!authHubAuth;
  }

  // ── Redirects to the hub ────────────────────────────────────────────────

  /**
   * Build the URL that the hub should redirect the user back to after
   * they've signed in. Includes the current room context so they return
   * to the same place.
   */
  buildReturnUrl() {
    return window.location.href;
  }

  redirectToLogin() {
    const returnUrl = encodeURIComponent(this.buildReturnUrl());
    window.location.href = `${this.hubUrl}/login?app=${this.appId}&return=${returnUrl}`;
  }

  redirectToSignup() {
    const returnUrl = encodeURIComponent(this.buildReturnUrl());
    window.location.href = `${this.hubUrl}/signup?app=${this.appId}&return=${returnUrl}`;
  }

  redirectToUpgrade() {
    const returnUrl = encodeURIComponent(this.buildReturnUrl());
    window.location.href = `${this.hubUrl}/subscription?app=${this.appId}&return=${returnUrl}`;
  }

  redirectToProfile() {
    const returnUrl = encodeURIComponent(this.buildReturnUrl());
    window.location.href = `${this.hubUrl}/profile?app=${this.appId}&return=${returnUrl}`;
  }

  // ── Token exchange on return ───────────────────────────────────────────

  /**
   * Handle the return from the auth hub. Looks for a custom token in the
   * URL fragment (#token=xyz) or query string (?token=xyz), signs the user
   * in via authHubAuth, then cleans the token out of the URL.
   *
   * Safe to call on every page load — no-ops if there's no token.
   *
   * @returns {Promise<boolean>} true if a sign-in happened
   */
  async handleAuthReturn() {
    if (!this.isEnabled()) return false;

    // Tokens arrive in the URL fragment (preferred — never sent to servers)
    // or query string (fallback). main.jsx lifts them into memory before
    // anything else runs (utils/urlSecrets.js), so the URL reads below are a
    // fallback only.
    const hash = window.location.hash;
    const hashParams = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
    const fragmentToken = hashParams.get('token');
    const queryToken = new URLSearchParams(window.location.search).get('token');
    const token = getUrlSecret('token') || fragmentToken || queryToken;

    if (!token) return false;

    // Clean the token out of the URL IMMEDIATELY (before anything can log it)
    if (fragmentToken || queryToken) {
      const cleanPath = window.location.pathname;
      const cleanSearch = fragmentToken ? window.location.search : '';
      window.history.replaceState({}, '', cleanPath + cleanSearch);
    }

    try {
      const userCredential = await signInWithCustomToken(authHubAuth, token);
      this.currentUser = userCredential.user;
      const idTokenResult = await userCredential.user.getIdTokenResult();
      this.userClaims = idTokenResult.claims;
      return true;
    } catch (error) {
      console.error('[AuthHub] Token exchange failed:', error);
      return false;
    } finally {
      // Used once (or failed): forget it.
      clearUrlSecrets(HUB_URL_PARAMS);
    }
  }

  /**
   * Force-refresh the user's ID token to pick up updated custom claims.
   * Call this after a purchase completes so the new `hostPass` claim
   * flows through without waiting for the normal refresh interval.
   */
  async refreshClaims() {
    if (!this.isEnabled() || !this.currentUser) return null;
    try {
      const idTokenResult = await this.currentUser.getIdTokenResult(true);
      this.userClaims = idTokenResult.claims;
      return this.userClaims;
    } catch (err) {
      console.error('[AuthHub] Failed to refresh claims:', err);
      return null;
    }
  }

  // ── Auth state listener ────────────────────────────────────────────────

  /**
   * Subscribe to auth hub auth state changes.
   * Fires with (user, claims) whenever the authenticated user changes.
   */
  onAuthStateChanged(callback) {
    if (!this.isEnabled()) {
      // Fire once with no user, then no-op
      callback(null, null);
      return () => {};
    }

    return onAuthStateChanged(authHubAuth, async (user) => {
      this.currentUser = user;
      if (user) {
        try {
          const idTokenResult = await user.getIdTokenResult();
          this.userClaims = idTokenResult.claims;
        } catch (err) {
          console.error('[AuthHub] Failed to get user claims:', err);
          this.userClaims = null;
        }
      } else {
        this.userClaims = null;
      }
      callback(user, this.userClaims);
    });
  }

  /**
   * Sign out of the auth hub session. DrawLab's own anonymous auth is
   * left untouched so the user can continue as a guest.
   */
  async signOut() {
    if (!this.isEnabled()) return;
    try {
      await fbSignOut(authHubAuth);
      this.currentUser = null;
      this.userClaims = null;
    } catch (err) {
      console.error('[AuthHub] Sign out failed:', err);
    }
  }

  // ── Entitlement checks ─────────────────────────────────────────────────

  /**
   * Is the user currently signed in via the auth hub?
   */
  isSignedIn() {
    return !!this.currentUser;
  }

  /**
   * Does the user have DrawLab Host Pass?
   * Host Pass is granted through either:
   *   1. A standalone DrawLab Host Pass purchase (via RevenueCat, stored
   *      in users/{uid}/appSubscriptions/draw-lab)
   *   2. Having Plus or Ultimate DDD subscription (cross-app benefit)
   *
   * The custom token from auth-hub carries both in its claims.
   */
  hasHostPass() {
    if (!this.isEnabled() || !this.userClaims) return false;

    // Standalone DrawLab Host Pass (from RevenueCat subscription)
    if (this.userClaims.apps?.drawLab?.hostPass) return true;

    // DDD ecosystem benefit — Plus and Ultimate tiers include Host Pass
    const plan = this.userClaims.subscription;
    if (plan === 'plus' || plan === 'ultimate') return true;

    return false;
  }

  /**
   * Get the display name for the signed-in user (if any).
   */
  getDisplayName() {
    if (!this.currentUser) return null;
    return this.currentUser.displayName || this.currentUser.email?.split('@')[0] || 'Member';
  }

  /**
   * Get the user's avatar URL (if any).
   */
  getPhotoURL() {
    return this.currentUser?.photoURL || null;
  }

  /**
   * Get the current subscription plan as a string.
   * Returns 'free', 'starter', 'plus', 'ultimate', or 'host-pass'.
   */
  getSubscriptionPlan() {
    if (!this.userClaims) return 'free';
    if (this.userClaims.apps?.drawLab?.hostPass) return 'host-pass';
    return this.userClaims.subscription || 'free';
  }
}

// Singleton instance
export const authHub = new AuthHubService();
export default authHub;
